import { useTranslation } from 'react-i18next'

import type { DeviceWarning } from './deviceWarning'
import { ReactComponent as CheckIcon } from 'assets/icons/Check.svg'
import { ReactComponent as HangupIcon } from 'assets/icons/EndCall.svg'
import { ReactComponent as FullscreenIcon } from 'assets/icons/Fullscreen.svg'
import { ReactComponent as FullscreenExitIcon } from 'assets/icons/FullscreenExit.svg'
import { ReactComponent as LinkIcon } from 'assets/icons/LinkChain.svg'
import { ReactComponent as MicIcon } from 'assets/icons/Microphone.svg'
import { ReactComponent as MicOffIcon } from 'assets/icons/MicrophoneOff.svg'
import { ReactComponent as CameraIcon } from 'assets/icons/Video.svg'
import { ReactComponent as CameraOffIcon } from 'assets/icons/VideoOff.svg'

import { ToolbarButton } from './ToolbarButton'
import { ToolbarDeviceMenu } from './ToolbarDeviceMenu'
import styles from './medentee.module.css'

export type ToolbarProps = {
  isMicOn: boolean
  isCameraOn: boolean
  micWarning?: DeviceWarning
  cameraWarning?: DeviceWarning
  onToggleMic: () => void
  onToggleCamera: () => void
  onHangup: () => void

  hangupLabel?: string

  buttonClassName?: string

  onCopyLink?: () => void
  isLinkCopied?: boolean

  onToggleFullscreen?: () => void
  isFullscreen?: boolean

  isCompact?: boolean
}

export function Toolbar({
  isMicOn,
  isCameraOn,
  micWarning,
  cameraWarning,
  onToggleMic,
  onToggleCamera,
  onHangup,
  hangupLabel,
  buttonClassName,
  onCopyLink,
  isLinkCopied,
  onToggleFullscreen,
  isFullscreen,
  isCompact
}: ToolbarProps) {
  const { t } = useTranslation()

  return (
    <div className={isCompact ? `${styles.toolbar} ${styles.toolbarCompact}` : styles.toolbar}>
      <div className={styles.toolbarBar}>
        <div className={styles.control}>
          <ToolbarButton
            label={t(
              isMicOn ? 'chat.call.toolbar.muteMicrophone' : 'chat.call.toolbar.unmuteMicrophone'
            )}
            icon={isMicOn ? <MicIcon aria-hidden /> : <MicOffIcon aria-hidden />}
            isOn={isMicOn}
            warning={micWarning}
            onClick={onToggleMic}
            extraClassName={buttonClassName}
          />
          <ToolbarDeviceMenu kind="audioinput" />
        </div>
        <div className={styles.control}>
          <ToolbarButton
            label={t(
              isCameraOn ? 'chat.call.toolbar.turnCameraOff' : 'chat.call.toolbar.turnCameraOn'
            )}
            icon={isCameraOn ? <CameraIcon aria-hidden /> : <CameraOffIcon aria-hidden />}
            isOn={isCameraOn}
            warning={cameraWarning}
            onClick={onToggleCamera}
            extraClassName={buttonClassName}
          />
          <ToolbarDeviceMenu kind="videoinput" />
        </div>
        {onCopyLink && (
          <ToolbarButton
            label={t(isLinkCopied ? 'chat.call.toolbar.linkCopied' : 'chat.call.toolbar.copyLink')}
            icon={isLinkCopied ? <CheckIcon aria-hidden /> : <LinkIcon aria-hidden />}
            isOn={isLinkCopied}
            onClick={onCopyLink}
            extraClassName={buttonClassName}
          />
        )}
        <ToolbarButton
          label={hangupLabel ?? t('chat.call.toolbar.leave')}
          icon={<HangupIcon width={26} height={26} aria-hidden />}
          isHangup
          onClick={onHangup}
          extraClassName={buttonClassName}
        />
        {onToggleFullscreen && (
          <ToolbarButton
            label={t(
              isFullscreen ? 'chat.call.toolbar.fullscreen_exit' : 'chat.call.toolbar.fullscreen'
            )}
            icon={
              isFullscreen ? <FullscreenExitIcon aria-hidden /> : <FullscreenIcon aria-hidden />
            }
            onClick={onToggleFullscreen}
            extraClassName={buttonClassName}
          />
        )}
      </div>
    </div>
  )
}
